/**
 * nodeRenderer.ts
 *
 * Shared rendering logic for orbital nodes used by D3.
 * Uses a transparent chrome image with SVG gradient for status colors.
 */

import * as d3 from 'd3';
import type { SatelliteSphere } from 'types/global';
import { NodeData } from './constants';
import { getStatusColor, getStatusFromSphere } from '../../../utils/statusColors';
import type { ExtendedStatusValue } from '../../../utils/statusColors';
import { getOrbitalImage, getOrbitalStatusColor, ORBITAL_RING_DIMENSIONS } from '../../../utils/orbitalImages';

/** Size calculation constants (matching OrbitalNodeAtom) */
const NODE_BASE_SIZE = 50;
const NODE_MAX_GROWTH = 30;

/**
 * Calculate orbital node size based on impact score
 */
export function calculateOrbitalNodeSize(impact: number): number {
  const normalizedImpact = Math.max(0, Math.min(100, impact)) / 100;
  return NODE_BASE_SIZE + normalizedImpact * NODE_MAX_GROWTH;
}

/**
 * Get initials from a name string
 */
function getInitials(name: string): string {
  if (!name) return '?';
  const words = name.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  return (words[0][0] + words[1][0]).toUpperCase();
}

/**
 * Render an orbital node into a D3 group element.
 * Structure:
 *   1. Status color ring (SVG circle with gradient)
 *   2. Chrome overlay image (transparent PNG)
 *   3. Inner background circle
 *   4. Avatar image or initials
 *
 * @param nodeGroup - D3 selection for the node group (already positioned)
 * @param node - NodeData for this node
 * @param defs - D3 selection for SVG defs (for gradients and clip paths)
 * @param orbitIndex - Index of the orbit this node belongs to
 * @param nodeIndex - Index of this node within its orbit
 */
export function renderOrbitalNode(
  nodeGroup: d3.Selection<SVGGElement, NodeData, SVGGElement, unknown>,
  node: NodeData,
  defs: d3.Selection<SVGDefsElement, unknown, null, undefined>,
  orbitIndex: number,
  nodeIndex: number
): void {
  // Calculate size
  const impact = node.impact ?? 50;
  const size = calculateOrbitalNodeSize(impact);
  const nodeRadius = size / 2;
  const innerRadius = nodeRadius * 0.52; // Inner content area
  const avatarRadius = nodeRadius * 0.4; // Avatar/icon size - fits inside chrome ring

  // Get status and colors
  const statusSource = node.sphereData || node.sphereNode;
  const status = statusSource ? getStatusFromSphere(statusSource) : 'unknown';
  const statusColor = getOrbitalStatusColor(status);
  const orbitalImage = getOrbitalImage();

  // Create gradient for status ring
  const gradientId = `status-gradient-orbit-${orbitIndex}-node-${nodeIndex}`;
  if (defs.select(`#${gradientId}`).empty()) {
    const gradient = defs.append('linearGradient')
      .attr('id', gradientId)
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '100%')
      .attr('y2', '100%');

    gradient.append('stop')
      .attr('offset', '0%')
      .attr('stop-color', statusColor)
      .attr('stop-opacity', 0.9);

    gradient.append('stop')
      .attr('offset', '50%')
      .attr('stop-color', statusColor)
      .attr('stop-opacity', 1);

    gradient.append('stop')
      .attr('offset', '100%')
      .attr('stop-color', statusColor)
      .attr('stop-opacity', 0.9);
  }

  // NOTE: Click target is now added LAST (after all visual elements) so it's on top

  // Layer 1: Status color ring (behind the chrome image)
  const ringRadius = nodeRadius * ORBITAL_RING_DIMENSIONS.ringCenterRadius;
  const strokeWidth = size * ORBITAL_RING_DIMENSIONS.strokeWidth;

  nodeGroup.append('circle')
    .attr('class', 'status-ring')
    .attr('cx', 0)
    .attr('cy', 0)
    .attr('r', ringRadius)
    .attr('fill', 'none')
    .attr('stroke', `url(#${gradientId})`)
    .attr('stroke-width', strokeWidth)
    .style('pointer-events', 'none');  // Don't block clicks

  // Layer 2: Chrome overlay image (transparent PNG)
  nodeGroup.append('image')
    .attr('class', 'orbital-chrome-image')
    .attr('href', orbitalImage)
    .attr('x', -nodeRadius)
    .attr('y', -nodeRadius)
    .attr('width', size)
    .attr('height', size)
    .attr('preserveAspectRatio', 'xMidYMid meet')
    .style('pointer-events', 'none');  // Don't block clicks

  // Layer 3: Inner circle (content background)
  nodeGroup.append('circle')
    .attr('class', 'inner-bg')
    .attr('cx', 0)
    .attr('cy', 0)
    .attr('r', innerRadius)
    .attr('fill', '#1a2744')
    .style('pointer-events', 'none');  // Don't block clicks

  // Layer 4: Avatar image or initials
  if (node.imagePath) {
    // Lazy load avatar images - store path in data-src, load via IntersectionObserver
    nodeGroup.append('image')
      .attr('class', 'orbital-avatar-image lazy-image')
      .attr('data-src', node.imagePath)
      .attr('x', -avatarRadius)
      .attr('y', -avatarRadius)
      .attr('width', avatarRadius * 2)
      .attr('height', avatarRadius * 2)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('pointer-events', 'none');  // Don't block clicks
  } else {
    // Show initials
    const name = node.customLabel || node.label || 'SP';
    const initials = getInitials(name);
    const fontSize = Math.max(10, nodeRadius * 0.45);

    nodeGroup.append('text')
      .attr('class', 'initials')
      .attr('x', 0)
      .attr('y', 0)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'central')
      .attr('fill', '#ffffff')
      .attr('font-size', fontSize)
      .attr('font-weight', '600')
      .attr('font-family', 'var(--font-family, sans-serif)')
      .style('pointer-events', 'none')  // Don't block clicks
      .text(initials);
  }

  // LAST LAYER: Click target on TOP of everything else
  // This ensures it receives all pointer events
  // NOTE: We use a nearly-transparent fill so the element is still painted and receives events
  nodeGroup.append('circle')
    .attr('class', 'click-target')
    .attr('cx', 0)
    .attr('cy', 0)
    .attr('r', nodeRadius)
    .attr('fill', 'rgba(0, 0, 0, 0.01)')
    .style('pointer-events', 'all')
    .style('cursor', 'pointer');
}

/**
 * Render satellites around a node.
 *
 * @param nodeGroup - D3 selection for the node group
 * @param satellites - Array of satellite data
 * @param satelliteCount - Fallback count if satellites array is empty
 */
export function renderSatellites(
  nodeGroup: d3.Selection<SVGGElement, NodeData, SVGGElement, unknown>,
  satellites: SatelliteSphere[],
  satelliteCount: number
): void {
  // Use satellites array if available, otherwise fall back to count
  const count = satellites.length > 0 ? Math.min(satellites.length, 5) : satelliteCount;

  if (count <= 0) return;

  const satelliteGroup = nodeGroup.append('g').attr('class', 'satellite-group');
  const orbitRadius = 35;

  // Satellite group should not block clicks on the main node
  satelliteGroup.style('pointer-events', 'none');

  // Add subtle orbit circle for satellites
  satelliteGroup
    .append('circle')
    .attr('class', 'satellite-orbit')
    .attr('r', orbitRadius)
    .attr('fill', 'none')
    .attr('stroke', '#7EDDF1')
    .attr('stroke-width', 2)
    .attr('opacity', 0.15)
    .style('pointer-events', 'none');  // Don't block clicks

  // Add satellite circles with status-based colors
  const satelliteSizes = [4, 3, 4, 3, 3];

  for (let i = 0; i < count; i++) {
    const angle = (i * 2 * Math.PI) / count;
    const satellite = satellites[i];
    // Get status color for this satellite (fallback to gray if no satellite data)
    const satelliteColor = satellite
      ? getStatusColor(satellite.status as ExtendedStatusValue)
      : '#9CA3AF';

    satelliteGroup
      .append('circle')
      .attr('class', `satellite-${i}`)
      .attr('r', satelliteSizes[i] ?? 3)
      .attr('fill', satelliteColor)
      .attr('stroke', satelliteColor)
      .attr('stroke-width', 1)
      .attr('cx', orbitRadius * Math.cos(angle))
      .attr('cy', orbitRadius * Math.sin(angle))
      .style('filter', 'drop-shadow(0 0 2px rgba(0, 0, 0, 0.3))');
  }
}

/**
 * Get the satellite count from node data.
 * Checks both satellites array and satelliteCount property.
 */
export function getSatelliteCount(node: NodeData): number {
  return node.satellites?.length || node.satelliteCount || 0;
}
